import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Award, BookOpen, CalendarCheck, ClipboardCheck, Download, FileText, Loader2,
  Mail, Printer, Share2, Sparkles, TrendingDown, TrendingUp, Trophy,
} from "lucide-react";
import { generatePerformanceInsights } from "@/lib/ai-insights.functions";
import { AICallout } from "@/components/makai/primitives";
import { AdaptiveInsightsButton } from "@/components/adaptive/adaptive-insights-button";

export const Route = createFileRoute("/learner/grades")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Grades & Reports — MAKAI" }] }),
  component: GradesPage,
});

type Tab = "overview" | "ca" | "exams" | "trends" | "attendance" | "card";

interface Term { id: string; name: string; start_date: string | null; end_date: string | null; is_current: boolean }
interface Assess { id: string; title: string; kind: string; max_score: number; weight: number; assessed_on: string | null; term_id: string | null; subject: { id: string; name: string } | null }
interface Mark { assessment_id: string; learner_id: string; score: number | null; grade: string | null; remarks: string | null }

function pctOf(score: number | null | undefined, max: number) {
  if (score == null || !max) return null;
  return (Number(score) / Number(max)) * 100;
}
function letterFor(pct: number | null): { g: string; cls: string; remark: string } {
  if (pct == null) return { g: "—", cls: "text-muted-foreground", remark: "Not assessed" };
  if (pct >= 80) return { g: "A", cls: "text-primary", remark: "Excellent" };
  if (pct >= 70) return { g: "B", cls: "text-sky-700", remark: "Very good" };
  if (pct >= 60) return { g: "C", cls: "text-gold-foreground", remark: "Good" };
  if (pct >= 50) return { g: "D", cls: "text-orange-600", remark: "Fair – needs effort" };
  return { g: "F", cls: "text-destructive", remark: "Below standard" };
}

function GradesPage() {
  const { ctx, loading, error } = useLearnerContext();
  const [tab, setTab] = useState<Tab>("overview");
  const [terms, setTerms] = useState<Term[]>([]);
  const [termId, setTermId] = useState<string>("");
  const [assessments, setAssessments] = useState<Assess[]>([]);
  const [myMarks, setMyMarks] = useState<Map<string, Mark>>(new Map());
  const [classMarks, setClassMarks] = useState<Mark[]>([]);
  const [attendance, setAttendance] = useState<{ status: string; date: string }[]>([]);
  const [parents, setParents] = useState<{ email: string | null; first_name: string; last_name: string }[]>([]);
  const [insights, setInsights] = useState<{ text: string; error: boolean } | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const aiFn = useServerFn(generatePerformanceInsights);
  const printRef = useRef<HTMLDivElement>(null);

  // Load terms + parents once
  useEffect(() => {
    if (!ctx) return;
    (async () => {
      const [{ data: t }, { data: pl }] = await Promise.all([
        supabase.from("terms").select("id, name, start_date, end_date, is_current").eq("school_id", ctx.schoolId).order("start_date", { ascending: false }),
        supabase.from("parent_learner_links").select("parents:parent_id(first_name, last_name, email)").eq("learner_id", ctx.learnerId),
      ]);
      const ts = (t ?? []) as Term[];
      setTerms(ts);
      const cur = ts.find((x) => x.is_current) ?? ts[0];
      setTermId(cur?.id ?? "");
      setParents(((pl ?? []) as { parents: { first_name: string; last_name: string; email: string | null } | null }[]).map((r) => r.parents!).filter(Boolean));
    })();
  }, [ctx]);

  // Load assessments / marks / attendance for active term
  useEffect(() => {
    if (!ctx || !ctx.streamId) return;
    const streamId = ctx.streamId;
    const learnerId = ctx.learnerId;
    (async () => {
      let aq = supabase.from("assessments")
        .select("id, title, kind, max_score, weight, assessed_on, term_id, subject:subjects_catalog(id, name)")
        .eq("stream_id", streamId).eq("status", "published")
        .order("assessed_on", { ascending: false, nullsFirst: false });
      if (termId) aq = aq.eq("term_id", termId);
      const { data: a } = await aq;
      const asmts = (a ?? []) as Assess[];
      setAssessments(asmts);
      const ids = asmts.map((x) => x.id);
      if (ids.length) {
        const { data: m } = await supabase.from("assessment_marks")
          .select("assessment_id, learner_id, score, grade, remarks").in("assessment_id", ids);
        const mine = new Map<string, Mark>();
        for (const row of (m ?? []) as Mark[]) if (row.learner_id === ctx.learnerId) mine.set(row.assessment_id, row);
        setMyMarks(mine);
        setClassMarks((m ?? []) as Mark[]);
      } else {
        setMyMarks(new Map()); setClassMarks([]);
      }
      const term = terms.find((t) => t.id === termId);
      const since = term?.start_date ?? new Date(Date.now() - 90 * 86400_000).toISOString().slice(0, 10);
      const until = term?.end_date ?? new Date().toISOString().slice(0, 10);
      const { data: att } = await supabase.from("attendance")
        .select("status, date").eq("learner_id", ctx.learnerId).gte("date", since).lte("date", until)
        .order("date");
      setAttendance(((att ?? []) as { status: string; date: string }[]));
    })();
  }, [ctx, termId, terms]);

  // Aggregations
  const bySubject = useMemo(() => {
    const map = new Map<string, { name: string; ca: { sum: number; w: number }; exam: { sum: number; w: number } }>();
    for (const a of assessments) {
      if (!a.subject) continue;
      const m = myMarks.get(a.id);
      const pct = pctOf(m?.score, a.max_score);
      if (pct == null) continue;
      const w = Number(a.weight || 1);
      const isExam = (a.kind || "").toLowerCase().includes("exam");
      const cur = map.get(a.subject.id) ?? { name: a.subject.name, ca: { sum: 0, w: 0 }, exam: { sum: 0, w: 0 } };
      const bucket = isExam ? cur.exam : cur.ca;
      bucket.sum += pct * w; bucket.w += w;
      map.set(a.subject.id, cur);
    }
    return Array.from(map.entries()).map(([id, v]) => {
      const ca = v.ca.w ? v.ca.sum / v.ca.w : null;
      const exam = v.exam.w ? v.exam.sum / v.exam.w : null;
      const overallW = (v.ca.w + v.exam.w) || 1;
      const overall = (v.ca.w + v.exam.w) ? (v.ca.sum + v.exam.sum) / overallW : null;
      return { id, name: v.name, ca, exam, overall };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [assessments, myMarks]);

  const overall = useMemo(() => {
    const vals = bySubject.map((s) => s.overall).filter((v): v is number => v != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [bySubject]);

  const classPosition = useMemo(() => {
    if (!ctx) return null;
    // overall per learner across selected term assessments
    const perLearner = new Map<string, { sum: number; w: number }>();
    for (const a of assessments) {
      const w = Number(a.weight || 1);
      for (const m of classMarks) {
        if (m.assessment_id !== a.id) continue;
        const pct = pctOf(m.score, a.max_score);
        if (pct == null) continue;
        const cur = perLearner.get(m.learner_id) ?? { sum: 0, w: 0 };
        cur.sum += pct * w; cur.w += w;
        perLearner.set(m.learner_id, cur);
      }
    }
    const ranked = Array.from(perLearner.entries())
      .map(([id, v]) => ({ id, avg: v.w ? v.sum / v.w : 0 }))
      .filter((r) => r.avg > 0)
      .sort((a, b) => b.avg - a.avg);
    const idx = ranked.findIndex((r) => r.id === ctx.learnerId);
    if (idx === -1 || ranked.length === 0) return null;
    return { rank: idx + 1, total: ranked.length, top: ranked[0]?.avg ?? 0 };
  }, [assessments, classMarks, ctx]);

  const attendancePct = useMemo(() => {
    if (!attendance.length) return 0;
    const ok = attendance.filter((a) => a.status === "present" || a.status === "late").length;
    return Math.round((ok / attendance.length) * 100);
  }, [attendance]);

  const attendanceBreakdown = useMemo(() => {
    const out = { present: 0, absent: 0, late: 0, excused: 0 };
    for (const a of attendance) {
      if (a.status === "present") out.present++;
      else if (a.status === "absent") out.absent++;
      else if (a.status === "late") out.late++;
      else if (a.status === "excused") out.excused++;
    }
    return out;
  }, [attendance]);

  // Trend across terms: subject overall per term
  const [trendData, setTrendData] = useState<{ subjects: string[]; rows: { term: string; values: Record<string, number | null> }[] }>({ subjects: [], rows: [] });
  useEffect(() => {
    if (!ctx?.streamId || !terms.length) return;
    const streamId = ctx.streamId;
    const learnerId = ctx.learnerId;
    (async () => {
      const { data: a } = await supabase.from("assessments")
        .select("id, max_score, weight, term_id, subject:subjects_catalog(id, name)")
        .eq("stream_id", streamId).eq("status", "published");
      const ids = ((a ?? []) as any[]).map((x) => x.id);
      if (!ids.length) { setTrendData({ subjects: [], rows: [] }); return; }
      const { data: m } = await supabase.from("assessment_marks").select("assessment_id, score").eq("learner_id", learnerId).in("assessment_id", ids);
      const byA = new Map((a ?? []).map((x: any) => [x.id, x]));
      const subjs = new Set<string>();
      const buckets = new Map<string, Map<string, { sum: number; w: number }>>(); // term -> subj -> {sum,w}
      for (const mk of m ?? []) {
        const asm: any = byA.get(mk.assessment_id); if (!asm?.subject) continue;
        const pct = pctOf(mk.score, asm.max_score); if (pct == null) continue;
        subjs.add(asm.subject.name);
        const t = asm.term_id ?? "—";
        const bs = buckets.get(t) ?? new Map(); buckets.set(t, bs);
        const cur = bs.get(asm.subject.name) ?? { sum: 0, w: 0 };
        cur.sum += pct * Number(asm.weight || 1); cur.w += Number(asm.weight || 1);
        bs.set(asm.subject.name, cur);
      }
      const ordered = [...terms].sort((a, b) => (a.start_date ?? "").localeCompare(b.start_date ?? ""));
      const rows = ordered.filter((t) => buckets.has(t.id)).map((t) => {
        const bs = buckets.get(t.id)!;
        const values: Record<string, number | null> = {};
        for (const s of subjs) {
          const v = bs.get(s);
          values[s] = v ? v.sum / v.w : null;
        }
        return { term: t.name, values };
      });
      setTrendData({ subjects: Array.from(subjs).sort(), rows });
    })();
  }, [ctx, terms]);

  function trendFor(subjectName: string): number | null {
    const rows = trendData.rows;
    if (rows.length < 2) return null;
    const last = rows[rows.length - 1].values[subjectName];
    const prev = rows[rows.length - 2].values[subjectName];
    if (last == null || prev == null) return null;
    return last - prev;
  }

  async function runAI() {
    if (!ctx) return;
    setAiLoading(true);
    try {
      const term = terms.find((t) => t.id === termId);
      const recent = assessments.slice(0, 8).map((a) => {
        const m = myMarks.get(a.id);
        return { title: a.title, subject: a.subject?.name ?? "—", pct: pctOf(m?.score, a.max_score) ?? 0 };
      }).filter((r) => r.pct > 0);
      const res = await aiFn({ data: {
        learnerName: `${ctx.firstName ?? "Learner"} ${ctx.lastName ?? ""}`.trim(),
        gradeLevel: ctx.gradeLevel,
        termLabel: term?.name ?? "Current term",
        subjects: bySubject.map((s) => ({ subject: s.name, average: s.overall ?? 0, trend: trendFor(s.name) })),
        attendancePct,
        classPosition: classPosition ? { rank: classPosition.rank, total: classPosition.total } : null,
        recentScores: recent,
      }});
      setInsights(res);
    } catch (e) {
      setInsights({ text: (e as Error).message, error: true });
    } finally { setAiLoading(false); }
  }

  function handlePrint() { window.print(); }
  function buildSummary() {
    const t = terms.find((x) => x.id === termId)?.name ?? "Current term";
    const lines = [
      `MAKAI — ${ctx?.firstName ?? "Learner"} ${ctx?.lastName ?? ""} · ${t}`,
      `Overall average: ${overall != null ? overall.toFixed(1) + "%" : "n/a"}`,
      classPosition ? `Class position: ${classPosition.rank} of ${classPosition.total}` : "",
      `Attendance: ${attendancePct}%`,
      "",
      "Subjects:",
      ...bySubject.map((s) => `  • ${s.name}: ${s.overall != null ? s.overall.toFixed(0) + "%" : "—"} (${letterFor(s.overall).g})`),
    ].filter(Boolean);
    return lines.join("\n");
  }
  function shareEmail() {
    const subject = encodeURIComponent(`Report card — ${ctx?.firstName ?? "Learner"}`);
    const body = encodeURIComponent(buildSummary());
    const to = parents.map((p) => p.email).filter(Boolean).join(",");
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  }
  async function copySummary() {
    try { await navigator.clipboard.writeText(buildSummary()); alert("Summary copied to clipboard."); } catch { /* noop */ }
  }

  if (loading) return <AppShell role="learner" title="Grades & Reports"><div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AppShell>;
  if (error) return <AppShell role="learner" title="Grades & Reports"><div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">{error}</div></AppShell>;

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: "overview", label: "Overview", icon: Trophy },
    { id: "ca", label: "Continuous", icon: ClipboardCheck },
    { id: "exams", label: "Exams", icon: FileText },
    { id: "trends", label: "Trends", icon: TrendingUp },
    { id: "attendance", label: "Attendance", icon: CalendarCheck },
    { id: "card", label: "Report card", icon: Award },
  ];

  return (
    <AppShell role="learner" title="Grades & Reports" subtitle="Track every score, position and trend in one place."
      action={
        <div className="flex flex-wrap items-center gap-2">
          <select value={termId} onChange={(e) => setTermId(e.target.value)} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
            {terms.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_current ? " · current" : ""}</option>)}
            {!terms.length && <option value="">All time</option>}
          </select>
          <Button variant="outline" size="sm" onClick={handlePrint}><Printer className="mr-1 h-4 w-4" /> Print</Button>
        </div>
      }>
      <style>{`@media print { body * { visibility: hidden !important } .print-area, .print-area * { visibility: visible !important } .print-area { position: absolute; inset: 0; padding: 24px } .no-print { display: none !important } }`}</style>

      <div className="no-print mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Trophy} label="Overall average" value={overall != null ? `${overall.toFixed(0)}%` : "—"} tint="from-primary to-gold" />
        <StatCard icon={Award} label="Class position" value={classPosition ? `${classPosition.rank} / ${classPosition.total}` : "—"} tint="from-sky-500 to-primary" />
        <StatCard icon={CalendarCheck} label="Attendance" value={`${attendancePct}%`} tint="from-emerald-500 to-emerald-700" />
        <StatCard icon={BookOpen} label="Subjects tracked" value={String(bySubject.length)} tint="from-gold to-orange-500" />
      </div>

      <div className="no-print mb-4 inline-flex flex-wrap gap-1 rounded-full border border-border bg-card p-1 text-sm shadow-soft">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-semibold transition ${tab === t.id ? "bg-gradient-african text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="no-print">
        {tab === "overview" && (
          <div className="grid gap-4 lg:grid-cols-[1.5fr_1fr]">
            <SubjectTable rows={bySubject} trendFor={trendFor} />
            <AIInsights insights={insights} loading={aiLoading} onRun={runAI} />
          </div>
        )}
        {tab === "ca" && <AssessmentList kindFilter="ca" assessments={assessments} myMarks={myMarks} classMarks={classMarks} learnerId={ctx?.learnerId ?? ""} />}
        {tab === "exams" && <AssessmentList kindFilter="exam" assessments={assessments} myMarks={myMarks} classMarks={classMarks} learnerId={ctx?.learnerId ?? ""} />}
        {tab === "trends" && <TrendsView data={trendData} />}
        {tab === "attendance" && <AttendanceView breakdown={attendanceBreakdown} records={attendance} pct={attendancePct} />}
        {tab === "card" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button onClick={handlePrint}><Download className="mr-1 h-4 w-4" /> Download PDF (Print → Save as PDF)</Button>
              <Button variant="outline" onClick={shareEmail} disabled={!parents.some((p) => p.email)}><Mail className="mr-1 h-4 w-4" /> Email parents</Button>
              <Button variant="outline" onClick={copySummary}><Share2 className="mr-1 h-4 w-4" /> Copy summary</Button>
            </div>
            <ReportCard
              learnerName={`${ctx?.firstName ?? ""} ${ctx?.lastName ?? ""}`.trim()}
              gradeLevel={ctx?.gradeLevel ?? null}
              termName={terms.find((t) => t.id === termId)?.name ?? "Current term"}
              rows={bySubject}
              overall={overall}
              classPosition={classPosition}
              attendancePct={attendancePct}
              parents={parents}
              printRef={printRef}
            />
          </div>
        )}
      </div>

      {/* Always-mounted printable region (shown only when printing) */}
      <div className="hidden print:block">
        <div className="print-area">
          <ReportCard
            learnerName={`${ctx?.firstName ?? ""} ${ctx?.lastName ?? ""}`.trim()}
            gradeLevel={ctx?.gradeLevel ?? null}
            termName={terms.find((t) => t.id === termId)?.name ?? "Current term"}
            rows={bySubject}
            overall={overall}
            classPosition={classPosition}
            attendancePct={attendancePct}
            parents={parents}
            printRef={printRef}
          />
        </div>
      </div>

      <div className="mt-6">
        <AICallout
          description="I can analyse your trend, flag weak topics and plan revision."
          suggestions={[
            "Explain my performance trend",
            "Where am I losing the most marks?",
            "Predict my end-of-term grade",
            "Suggest a revision plan for my weakest subject",
          ]}
        />
      </div>
    </AppShell>
  );
}

function StatCard({ icon: Icon, label, value, tint }: { icon: any; label: string; value: string; tint: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className={`grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br ${tint} text-white`}><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-display text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function SubjectTable({ rows, trendFor }: { rows: { id: string; name: string; ca: number | null; exam: number | null; overall: number | null }[]; trendFor: (s: string) => number | null }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
      <div className="border-b border-border p-4">
        <h3 className="font-display text-lg font-bold">Subject report</h3>
        <p className="text-xs text-muted-foreground">Continuous assessment, exams, and overall mark per subject.</p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
          <tr><th className="px-3 py-2">Subject</th><th className="px-3 py-2 text-center">CA</th><th className="px-3 py-2 text-center">Exam</th><th className="px-3 py-2 text-center">Overall</th><th className="px-3 py-2 text-center">Grade</th><th className="px-3 py-2 text-center">Trend</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const g = letterFor(r.overall);
            const t = trendFor(r.name);
            return (
              <tr key={r.id} className="border-t border-border">
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 text-center">{r.ca != null ? `${r.ca.toFixed(0)}%` : "—"}</td>
                <td className="px-3 py-2 text-center">{r.exam != null ? `${r.exam.toFixed(0)}%` : "—"}</td>
                <td className="px-3 py-2 text-center font-semibold">{r.overall != null ? `${r.overall.toFixed(0)}%` : "—"}</td>
                <td className={`px-3 py-2 text-center font-display font-bold ${g.cls}`}>{g.g}</td>
                <td className="px-3 py-2 text-center">
                  {t == null ? <span className="text-muted-foreground">—</span> :
                    t >= 0 ? <span className="inline-flex items-center gap-1 text-primary"><TrendingUp className="h-3.5 w-3.5" />+{t.toFixed(0)}</span> :
                    <span className="inline-flex items-center gap-1 text-destructive"><TrendingDown className="h-3.5 w-3.5" />{t.toFixed(0)}</span>}
                </td>
              </tr>
            );
          })}
          {!rows.length && <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-muted-foreground">No marks published yet for this term.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AIInsights({ insights, loading, onRun }: { insights: { text: string; error: boolean } | null; loading: boolean; onRun: () => void }) {
  return (
    <div className="rounded-2xl border border-border bg-gradient-to-br from-primary-soft to-gold/10 p-5 shadow-soft">
      <div className="flex items-center justify-between">
        <h3 className="inline-flex items-center gap-2 font-display text-lg font-bold"><Sparkles className="h-4 w-4 text-primary" /> AI performance coach</h3>
        <Button size="sm" onClick={onRun} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}</Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Personalised feedback based on your scores, trends and attendance.</p>
      <div className="mt-3 min-h-[160px] rounded-xl border border-border bg-card/70 p-3 text-sm leading-relaxed">
        {loading ? <div className="grid h-full place-items-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          : insights ? <pre className={`whitespace-pre-wrap font-sans ${insights.error ? "text-destructive" : ""}`}>{insights.text}</pre>
          : <div className="text-muted-foreground">Click Generate to get a personalised coaching summary.</div>}
      </div>
    </div>
  );
}

function AssessmentList({ kindFilter, assessments, myMarks, classMarks, learnerId }: { kindFilter: "ca" | "exam"; assessments: Assess[]; myMarks: Map<string, Mark>; classMarks: Mark[]; learnerId: string }) {
  const filtered = assessments.filter((a) => {
    const isExam = (a.kind || "").toLowerCase().includes("exam");
    return kindFilter === "exam" ? isExam : !isExam;
  });
  function classAvgFor(id: string, max: number) {
    const scores = classMarks.filter((m) => m.assessment_id === id && m.score != null).map((m) => pctOf(m.score, max) ?? 0);
    if (!scores.length) return null;
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
          <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Subject</th><th className="px-3 py-2">Title</th><th className="px-3 py-2 text-center">My score</th><th className="px-3 py-2 text-center">Class avg</th><th className="px-3 py-2 text-center">Grade</th><th className="px-3 py-2 text-center">AI</th></tr>
        </thead>
        <tbody>
          {filtered.map((a) => {
            const m = myMarks.get(a.id);
            const pct = pctOf(m?.score, a.max_score);
            const g = letterFor(pct);
            const cls = classAvgFor(a.id, a.max_score);
            return (
              <tr key={a.id} className="border-t border-border">
                <td className="px-3 py-2 text-muted-foreground">{a.assessed_on ?? "—"}</td>
                <td className="px-3 py-2">{a.subject?.name ?? "—"}</td>
                <td className="px-3 py-2 font-medium">{a.title}</td>
                <td className="px-3 py-2 text-center">{m?.score != null ? `${m.score}/${a.max_score} · ${pct?.toFixed(0)}%` : <span className="text-muted-foreground">Pending</span>}</td>
                <td className="px-3 py-2 text-center text-muted-foreground">{cls != null ? `${cls.toFixed(0)}%` : "—"}</td>
                <td className={`px-3 py-2 text-center font-display font-bold ${g.cls}`}>{g.g}</td>
                <td className="px-3 py-2 text-center">
                  {m?.score != null && a.max_score && learnerId ? (
                    <AdaptiveInsightsButton
                      learnerId={learnerId}
                      sourceType={kindFilter === "exam" ? "exam" : "quiz"}
                      sourceId={a.id}
                      subject={a.subject?.name ?? "General"}
                      topic={a.title}
                      percent={pct ?? 0}
                      totalMarks={Number(a.max_score)}
                      earnedMarks={Number(m.score)}
                      label="Analyse"
                    />
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </td>
              </tr>
            );
          })}
          {!filtered.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-muted-foreground">Nothing recorded yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function TrendsView({ data }: { data: { subjects: string[]; rows: { term: string; values: Record<string, number | null> }[] } }) {
  if (!data.rows.length) return <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">Need at least one term of marks to plot a trend.</div>;
  const palette = ["#16a34a","#0ea5e9","#f59e0b","#ef4444","#8b5cf6","#ec4899","#14b8a6","#f97316"];
  const W = 640, H = 260, P = 36;
  const xs = data.rows.length;
  const xStep = xs > 1 ? (W - 2 * P) / (xs - 1) : 0;
  function yFor(v: number) { return H - P - (v / 100) * (H - 2 * P); }
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <h3 className="font-display text-lg font-bold">Performance trends</h3>
      <p className="text-xs text-muted-foreground">Subject averages across terms.</p>
      <div className="mt-4 overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[480px]">
          {[0, 25, 50, 75, 100].map((g) => (
            <g key={g}>
              <line x1={P} x2={W - P} y1={yFor(g)} y2={yFor(g)} stroke="currentColor" className="text-border" strokeDasharray="3 3" />
              <text x={4} y={yFor(g) + 4} className="fill-muted-foreground text-[10px]">{g}</text>
            </g>
          ))}
          {data.rows.map((r, i) => (
            <text key={r.term} x={P + i * xStep} y={H - 8} textAnchor="middle" className="fill-muted-foreground text-[10px]">{r.term}</text>
          ))}
          {data.subjects.map((s, si) => {
            const pts = data.rows.map((r, i) => ({ x: P + i * xStep, y: r.values[s] != null ? yFor(r.values[s]!) : null }));
            const d = pts.filter((p) => p.y != null).map((p, idx) => `${idx === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
            return (
              <g key={s}>
                <path d={d} fill="none" stroke={palette[si % palette.length]} strokeWidth={2} />
                {pts.map((p, i) => p.y != null && <circle key={i} cx={p.x} cy={p.y} r={3.5} fill={palette[si % palette.length]} />)}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-xs">
        {data.subjects.map((s, i) => (
          <span key={s} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: palette[i % palette.length] }} />{s}</span>
        ))}
      </div>
    </div>
  );
}

function AttendanceView({ breakdown, records, pct }: { breakdown: { present: number; absent: number; late: number; excused: number }; records: { status: string; date: string }[]; pct: number }) {
  const total = records.length;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
        <h3 className="font-display text-lg font-bold">Attendance summary</h3>
        <p className="text-xs text-muted-foreground">{total} days recorded · {pct}% attendance.</p>
        <div className="mt-4 space-y-2">
          {(["present","late","excused","absent"] as const).map((k) => {
            const v = breakdown[k];
            const p = total ? Math.round((v / total) * 100) : 0;
            const color = k === "present" ? "bg-primary" : k === "late" ? "bg-gold" : k === "excused" ? "bg-sky-500" : "bg-destructive";
            return (
              <div key={k}>
                <div className="flex justify-between text-sm capitalize"><span>{k}</span><span className="text-muted-foreground">{v} ({p}%)</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted"><div className={`h-full ${color}`} style={{ width: `${p}%` }} /></div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
        <h3 className="font-display text-lg font-bold">Daily log</h3>
        <div className="mt-3 grid max-h-[360px] grid-cols-7 gap-1 overflow-y-auto pr-1">
          {records.map((r) => {
            const c = r.status === "present" ? "bg-primary" : r.status === "late" ? "bg-gold" : r.status === "excused" ? "bg-sky-500" : "bg-destructive";
            return <div key={r.date} title={`${r.date} · ${r.status}`} className={`h-7 rounded-md ${c}`} />;
          })}
          {!records.length && <div className="col-span-7 py-8 text-center text-sm text-muted-foreground">No attendance recorded.</div>}
        </div>
      </div>
    </div>
  );
}

function ReportCard({
  learnerName, gradeLevel, termName, rows, overall, classPosition, attendancePct, parents, printRef,
}: {
  learnerName: string; gradeLevel: string | null; termName: string;
  rows: { id: string; name: string; ca: number | null; exam: number | null; overall: number | null }[];
  overall: number | null; classPosition: { rank: number; total: number } | null; attendancePct: number;
  parents: { first_name: string; last_name: string; email: string | null }[];
  printRef: React.RefObject<HTMLDivElement | null>;
}) {
  const g = letterFor(overall);
  return (
    <div ref={printRef} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
      <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">MAKAI · Report Card</div>
          <h2 className="font-display text-2xl font-bold">{learnerName || "Learner"}</h2>
          <div className="text-sm text-muted-foreground">{gradeLevel ?? "—"} · {termName}</div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl font-bold text-primary">{overall != null ? `${overall.toFixed(0)}%` : "—"}</div>
          <div className={`text-sm font-semibold ${g.cls}`}>{g.g} · {g.remark}</div>
          {classPosition && <div className="text-xs text-muted-foreground">Position {classPosition.rank} of {classPosition.total}</div>}
          <div className="text-xs text-muted-foreground">Attendance {attendancePct}%</div>
        </div>
      </div>
      <table className="mt-4 w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr><th className="py-2">Subject</th><th className="py-2 text-center">CA</th><th className="py-2 text-center">Exam</th><th className="py-2 text-center">Overall</th><th className="py-2 text-center">Grade</th><th className="py-2">Remark</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const gg = letterFor(r.overall);
            return (
              <tr key={r.id} className="border-t border-border">
                <td className="py-2 font-medium">{r.name}</td>
                <td className="py-2 text-center">{r.ca != null ? `${r.ca.toFixed(0)}%` : "—"}</td>
                <td className="py-2 text-center">{r.exam != null ? `${r.exam.toFixed(0)}%` : "—"}</td>
                <td className="py-2 text-center font-semibold">{r.overall != null ? `${r.overall.toFixed(0)}%` : "—"}</td>
                <td className={`py-2 text-center font-display font-bold ${gg.cls}`}>{gg.g}</td>
                <td className="py-2 text-muted-foreground">{gg.remark}</td>
              </tr>
            );
          })}
          {!rows.length && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No marks for this term.</td></tr>}
        </tbody>
      </table>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-border bg-muted/40 p-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Class teacher's comment</div>
          <div className="mt-1 text-sm">A solid term. Keep building on your strengths and stay consistent with revision.</div>
        </div>
        <div className="rounded-xl border border-border bg-muted/40 p-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Parents / Guardians</div>
          <div className="mt-1 text-sm">{parents.length ? parents.map((p) => `${p.first_name} ${p.last_name}`).join(", ") : "Not linked yet."}</div>
        </div>
      </div>
      <div className="mt-6 flex items-center justify-between text-xs text-muted-foreground">
        <span>Generated by MAKAI · {new Date().toLocaleDateString()}</span>
        <span>Signature: ____________________</span>
      </div>
    </div>
  );
}
